/**
 * AppsPage — list of deployed managed-app instances for a project.
 *
 * Mounted at /projects/:projectId/apps as a child of ProjectLayout (so
 * the sidebar / header / project context come from the layout). Just
 * the list content lives here.
 *
 * Data: GET /apps?projectId=<id> returns the project's deployed app
 * rows newest-first. Click a row → /projects/:projectId/apps/:instanceId.
 *
 * Visual language is a deliberate clone of PipelinesPage:
 *   - same `pipelines-page` wrapper + `pipelines-header*` chrome
 *   - same shared DataTable atom with column class names from the
 *     pipelines stylesheet
 *   - same dark theme + no new colors
 * Deploying a new app goes through the marketplace, same as deploying
 * a new pipeline goes through the workflow marketplace.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { openOutline } from 'ionicons/icons';
import { IonIcon } from '@ionic/react';
import DataTable from '../../components/shared/DataTable/DataTable';
import AppStatus from './AppStatus';
import AppsDemo from '../../components/shared/GetStartedSteps/AppsDemo';
import { Apps } from '../../services/apps';
import { timeAgo } from '../../utils/timeAgo';
// Reuse the pipelines stylesheet for header / wrapper / column widths so
// the two list pages look like siblings.
import { projectAppsMarketplacePath } from '../../utils/marketplacePaths';
import '../PipelinesPage/PipelinesPage.css';

const AppsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { setToast } = useOutletContext() || {};

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  // Toaster ref so re-renders don't bust fetchApps' identity (same
  // pattern PipelinesPage uses — see the comment there for context).
  const setToastRef = useRef(setToast);
  useEffect(() => { setToastRef.current = setToast; }, [setToast]);

  const fetchApps = useCallback(async (opts = {}) => {
    const { isRefetch = false } = opts;
    if (!isRefetch) setLoading(true);
    try {
      // Two sources, merged:
      //  1. Project-scoped cloud apps (Fargate). These carry a projectId
      //     and are indexed by ProjectCreatedAtIndex — the only rows the
      //     `?projectId=` query returns.
      //  2. Account-scoped solo apps (dedicated EC2). Solo rows have NO
      //     projectId, so they are absent from the GSI above — they only
      //     appear in the account-wide list (`GET /apps` with no param).
      //     We pull that list and keep ONLY deployMode==='solo' rows so we
      //     don't surface other projects' cloud apps on a project page.
      const [projResp, acctResp] = await Promise.all([
        Apps.listInstances({ projectId }),
        Apps.listInstances({}).catch((e) => {
          // Solo is additive — a failure here must not blank the cloud list.
          console.error('Failed to fetch solo apps:', e);
          return { apps: [] };
        }),
      ]);
      const cloud = Array.isArray(projResp?.apps) ? projResp.apps : [];
      const solo = (Array.isArray(acctResp?.apps) ? acctResp.apps : [])
        .filter((a) => a.deployMode === 'solo' && a.status !== 'terminated');
      setApps([...cloud, ...solo]);
    } catch (err) {
      console.error('Failed to fetch apps:', err);
      setToastRef.current?.({ isOpen: true, message: `Could not load apps: ${err.message}`, color: 'danger' });
      if (!isRefetch) setApps([]);
    } finally {
      if (!isRefetch) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  // ── solo helpers ─────────────────────────────────────────────────────
  // Solo apps (deployMode==='solo') run on a dedicated EC2, are keyed by
  // appSlug (not instanceId), expose a public hostname (`domain`), and
  // MUST be torn down via DELETE /apps/solo/{slug} so the VM + Elastic IP
  // + Route53 record are all cleaned up. Cloud apps are unchanged.
  const isSolo = (a) => a?.deployMode === 'solo';
  // Stable row key: solo rows are keyed by slug (instanceId churns on
  // resume), cloud rows by instanceId.
  const keyFor = (a) => (isSolo(a) ? `solo:${a.appSlug}` : a.instanceId);
  // Public URL: solo prefers its trusted https domain, falling back to
  // the raw public IP (http, pre-DNS). Cloud keeps url/subdomain.
  const urlFor = (a) => {
    if (isSolo(a)) {
      if (a.domain) return `https://${a.domain}`;
      if (a.publicIp) return `http://${a.publicIp}`;
      return null;
    }
    return a.url || (a.subdomain ? `https://${a.subdomain}` : null);
  };
  // Solo status for the pill: prefer the lifecycle `status`
  // (active/paused/terminated), but while provisioning surface the
  // finer-grained `phase` (provisioning/running/failed).
  const statusFor = (a) => {
    if (isSolo(a)) {
      if (a.status === 'active') return a.phase || a.status;
      return a.status || a.phase;
    }
    return a.status;
  };

  // ── columns ────────────────────────────────────────────────────────
  // Mirror the Pipelines columns: NAME / STATUS / VERSION-equivalent /
  // LAST DEPLOYED. The app-specific "VERSION" column is the app's
  // catalog version (n8n v1.97.1 etc) since users care about that more
  // than the instance row's internal version number.
  const columns = [
    {
      header: 'NAME',
      key: 'name',
      className: 'pipelines-col-name',
      render: (a) => (
        <span className="pipelines-name-cell">
          <span className="pipelines-name">{a.name || a.appType || a.appSlug || a.instanceId}</span>
          <span className="apps-deploy-badge" data-mode={isSolo(a) ? 'solo' : 'cloud'}>
            {isSolo(a) ? 'Dedicated' : 'Cloud'}
          </span>
        </span>
      ),
    },
    {
      header: 'STATUS',
      key: 'status',
      className: 'pipelines-col-status',
      render: (a) => (
        <AppStatus
          status={statusFor(a)}
          runningCount={a.runningCount}
          desiredCount={a.desiredCount}
          variant="table"
        />
      ),
    },
    {
      header: 'APP',
      key: 'appType',
      className: 'pipelines-col-version',
      render: (a) => (
        <span className="pipelines-version">
          {isSolo(a)
            ? `${a.appSlug}${a.tier ? ` · ${a.tier}` : ''}`
            : `${a.appType || ''}${a.appVersion ? ` v${a.appVersion}` : ''}`}
        </span>
      ),
    },
    {
      header: 'CREATED',
      key: 'createdAt',
      className: 'pipelines-col-deployed',
      render: (a) => (
        <span className="pipelines-deployed">
          {a.createdAt ? timeAgo(a.createdAt) : '—'}
        </span>
      ),
    },
  ];

  const actions = [
    {
      label: 'View details',
      // Cloud apps have a project-scoped detail page. Solo apps have no
      // projectId (so /projects/:projectId/apps/:instanceId would be a
      // broken /projects/undefined/... route) — open the live app URL
      // instead until a dedicated solo detail page exists.
      onClick: (a) => {
        if (isSolo(a)) {
          const url = urlFor(a);
          if (url) window.open(url, '_blank', 'noopener,noreferrer');
          return;
        }
        navigate(`/projects/${projectId}/apps/${a.instanceId}`);
      },
    },
    {
      label: 'Open app',
      onClick: (a) => {
        const url = urlFor(a);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      },
    },
    { divider: true },
    {
      label: 'Destroy',
      danger: true,
      onClick: async (a) => {
        const name = a.name || a.appSlug || a.instanceId;
        if (isSolo(a)) {
          // SOLO teardown — DELETE /apps/solo/{slug}. This terminates the
          // VM, releases its Elastic IP, and removes the Route53 record.
          // Routing it through the cloud destroy(instanceId) would orphan
          // the VM (still billed) and leave dangling DNS — the bug we fix.
          if (!window.confirm(
            `Destroy dedicated app "${name}"? Stops the VM and releases its IP + DNS. ` +
            `Data buckets (backups) are retained. This cannot be undone.`
          )) return;
          try {
            await Apps.destroySolo(a.appSlug);
            setApps((prev) => prev.filter((x) => keyFor(x) !== keyFor(a)));
            setToast?.({ isOpen: true, message: `App "${name}" destroyed`, color: 'success' });
          } catch (err) {
            setToast?.({ isOpen: true, message: `Destroy failed: ${err.message}`, color: 'danger' });
          }
          return;
        }
        if (!window.confirm(`Destroy app "${name}"? Running task will be stopped. This cannot be undone.`)) return;
        try {
          await Apps.destroy(a.instanceId);
          setApps((prev) => prev.filter((x) => x.instanceId !== a.instanceId));
          setToast?.({ isOpen: true, message: `App "${name}" destroyed`, color: 'success' });
        } catch (err) {
          setToast?.({ isOpen: true, message: `Destroy failed: ${err.message}`, color: 'danger' });
        }
      },
    },
  ];

  return (
    <>
      <Helmet>
        <title>Apps · Zibby</title>
      </Helmet>

      {/* Empty state matches Pipelines exactly — the AppsDemo terminal
          mockup sits in a .pipelines-empty wrapper, no outer page
          padding. WorkflowDemo / AppsDemo handle their own chrome. */}
      {!loading && apps.length === 0 ? (
        <div className="pipelines-empty">
          <AppsDemo />
        </div>
      ) : (
        <div className="pipelines-page">
          {!loading && apps.length > 0 && (
            <div className="pipelines-header">
              <div className="pipelines-header-row">
                <h1 className="pipelines-title">Apps</h1>
              </div>
              <p className="pipelines-subtitle">
                Managed app instances deployed in this project.
                {' '}Deploy a new one from the{' '}
                <Link to={projectAppsMarketplacePath(projectId)} className="pipelines-subtitle-link">
                  app marketplace
                </Link>.
              </p>
            </div>
          )}

          <DataTable
            data={apps}
            columns={columns}
            loading={loading}
            rowKey="instanceId"
            actions={actions}
            onRowClick={(a) => {
              // Solo rows have no project detail page — clicking opens the
              // live app (or no-ops if it has no URL yet) instead of
              // navigating to a broken /projects/undefined route.
              if (isSolo(a)) {
                const url = urlFor(a);
                if (url) window.open(url, '_blank', 'noopener,noreferrer');
                return;
              }
              navigate(`/projects/${projectId}/apps/${a.instanceId}`);
            }}
          />
        </div>
      )}
    </>
  );
};

export default AppsPage;
